import React, { memo, useState, useCallback } from "react";
import { Button as AntButton } from "antd";
import type { ButtonProps as AntButtonProps } from "antd";
import { cn } from "src/util/cn";
import { HEIGHT_MAP, VARIANT_STYLES, ICON_SIZE_MAP } from "./constants";
import type { ButtonSize, ButtonVariant, ButtonTone } from "./constants";
import "./CustomButton.css";

export interface CustomButtonProps extends Omit<AntButtonProps, "type" | "size"> {
  label?: string;
  buttonSize?: ButtonSize;
  type?: ButtonVariant;
  tone?: ButtonTone;
  danger?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const CUSTOM_VARIANTS: ButtonVariant[] = ["primary", "secondary", "link", "icon", "border"];

const CustomButton: React.FC<CustomButtonProps> = ({
  label,
  buttonSize = "medium",
  type = "primary",
  tone = "default",
  danger = false,
  className,
  style,
  children,
  disabled,
  icon,
  loading,
  htmlType = "button",
  ...rest
}) => {
  const [isHover, setIsHover] = useState(false);
  const [isFocus, setIsFocus] = useState(false);

  const isCustomVariant = CUSTOM_VARIANTS.includes(type);
  const isIconOnly = type === "icon" || (type === "primary" && !children && !label && icon);

  const getVariantKey = (): keyof typeof VARIANT_STYLES => {
    if (danger) return type === "primary" ? "danger-primary" : "danger-secondary";
    if (type === "primary") return "primary";
    if (type === "secondary" || type === "border") return "secondary";
    return "primary";
  };

  const getState = (): "default" | "hover" | "focus" | "disabled" => {
    if (disabled) return "disabled";
    if (isFocus) return "focus";
    if (isHover) return "hover";
    return "default";
  };

  const variantKey = getVariantKey();
  const stateKey = getState();
  const variantStyles = isCustomVariant ? VARIANT_STYLES[variantKey][stateKey] : undefined;

  const height = HEIGHT_MAP[buttonSize];
  const inlineStyles: React.CSSProperties = {
    ...style,
    ...(isCustomVariant && variantStyles ? variantStyles : {}),
    ...(isCustomVariant ? { height, minHeight: height } : {}),
    ...(isIconOnly ? { width: ICON_SIZE_MAP[buttonSize], minWidth: ICON_SIZE_MAP[buttonSize], padding: 0 } : {}),
  };

  const onMouseEnter = useCallback(() => setIsHover(true), []);
  const onMouseLeave = useCallback(() => setIsHover(false), []);
  const onFocus = useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      rest.onFocus?.(e);
      setIsFocus(true);
    },
    [rest.onFocus]
  );
  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      rest.onBlur?.(e);
      setIsFocus(false);
    },
    [rest.onBlur]
  );

  if (isCustomVariant) {
    return (
      <button
        type={htmlType === "submit" ? "submit" : htmlType === "reset" ? "reset" : "button"}
        className={cn("custom-button-variant", isIconOnly && "custom-button-icon-only", className)}
        style={inlineStyles}
        disabled={disabled}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={rest.onClick}
      >
        {loading ? (
          <span className="anticon anticon-loading anticon-spin" />
        ) : (
          <>
            {icon}
            {label ?? children}
          </>
        )}
      </button>
    );
  }

  const antType = type === "text" ? "text" : type === "dashed" ? "dashed" : "default";
  return (
    <AntButton
      type={antType}
      danger={danger}
      size={buttonSize === "small" ? "small" : buttonSize === "big" ? "large" : "middle"}
      className={className}
      style={style}
      disabled={disabled}
      icon={icon}
      loading={loading}
      {...rest}
    >
      {label ?? children}
    </AntButton>
  );
};

export default memo(CustomButton);
